import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import type { IssueKey } from '@atlassian/jira-shared-types/src/general.tsx';
import type { CardSetMeta, RetryableActionBuilder } from '../meta/card-set-meta.tsx';
import {
	type OptimisticId,
	type OptimisticUiMeta,
	commitOptimisticUiMeta,
	revertOptimisticUiMeta,
} from '../meta/optimistic-ui-meta.tsx';

export const CARD_SET_REQUEST = 'state.actions.card-set.CARD_SET_REQUEST';

export type CardSetRequestAction = {
	type: typeof CARD_SET_REQUEST;
	payload: CardSetRequestActionPayload;
};

export type CardSetRequestActionPayload = {
	batchId: string;
	setMutations: CardSetMeta[];
};

export const cardSetRequest = (
	setMutations: CardSetMeta[],
	batchId: string,
): CardSetRequestAction => ({
	type: CARD_SET_REQUEST,
	payload: {
		setMutations,
		batchId,
	},
});

export const CARD_SET_INDIVIDUAL_REQUEST_SUCCESS =
	'state.actions.card-set.CARD_SET_INDIVIDUAL_REQUEST_SUCCESS';

export type CardSetIndividualResponseSuccessAction = {
	type: typeof CARD_SET_INDIVIDUAL_REQUEST_SUCCESS;
	payload: CardSetIndividualResponseSuccessActionPayload;
	meta: OptimisticUiMeta & {
		analyticsEvent: UIAnalyticsEvent;
	};
};

export type CardSetIndividualResponseSuccessActionPayload = {
	issueKey: IssueKey;
	batchId: string;
	fieldId: string;
};

export const cardSetIndividualResponseSuccess = (
	payload: CardSetIndividualResponseSuccessActionPayload,
	optimisticId: OptimisticId,
	analyticsEvent: UIAnalyticsEvent,
): CardSetIndividualResponseSuccessAction => ({
	type: CARD_SET_INDIVIDUAL_REQUEST_SUCCESS,
	payload,
	meta: { ...commitOptimisticUiMeta(optimisticId), analyticsEvent },
});

export const CARD_SET_INDIVIDUAL_REQUEST_FAILURE =
	'state.actions.card-set.CARD_SET_INDIVIDUAL_REQUEST_FAILURE';

export type CardSetIndividualResponseFailureAction = {
	type: typeof CARD_SET_INDIVIDUAL_REQUEST_FAILURE;
	payload: CardSetIndividualResponseFailureActionPayload;
	meta: OptimisticUiMeta & {
		analyticsEvent: UIAnalyticsEvent;
	};
};

export type CardSetIndividualResponseFailureReason = 'network' | 'validation' | 'server';

export type CardSetIndividualResponseFailureActionPayload =
	CardSetIndividualResponseSuccessActionPayload & {
		retryAction: RetryableActionBuilder;
		failureReason: CardSetIndividualResponseFailureReason;
		statusCode?: number;
	};

export const cardSetIndividualResponseFailure = (
	payload: CardSetIndividualResponseFailureActionPayload,
	optimisticId: OptimisticId,
	analyticsEvent: UIAnalyticsEvent,
): CardSetIndividualResponseFailureAction => ({
	type: CARD_SET_INDIVIDUAL_REQUEST_FAILURE,
	payload,
	meta: {
		...revertOptimisticUiMeta(optimisticId),
		analyticsEvent,
	},
});

export type Action =
	| CardSetRequestAction
	| CardSetIndividualResponseFailureAction
	| CardSetIndividualResponseSuccessAction;
